import React, { useState } from 'react';
import { Auth } from 'aws-amplify';
import QrCodeImage from './QrCodeImage';
import sleep from '../../utils/sleep';

/** Cognito PostAuthentication Lambda sometimes times out; a quick retry often succeeds. */
const POST_AUTH_LAMBDA_SOCKET_TIMEOUT_FINAL_MESSAGE =
  'Please try again in 5 minutes.';

function getErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    return String((err as { message: unknown }).message);
  }
  return '';
}

// Cognito errors expose the type under different keys depending on how the
// SDK surfaces them: `code` (Amplify-wrapped), `__type` (raw Cognito JSON),
// or `name`. Check all three.
function getErrorCode(err: unknown): string {
  if (err && typeof err === 'object') {
    const e = err as Record<string, unknown>;
    return String(e.code ?? e.__type ?? e.name ?? '');
  }
  return '';
}

function isPostAuthenticationLambdaSocketTimeoutError(err: unknown): boolean {
  const msg = getErrorMessage(err).toLowerCase();
  if (!msg.includes('postauthentication')) return false;
  // Cognito surfaces the transient Lambda failure with varied wording; match the
  // known timeout/connection-reset phrasings rather than one exact string.
  const isTransient =
    msg.includes('socket timeout') ||
    msg.includes('timed out') ||
    msg.includes('timeout') ||
    msg.includes('socket hang up') ||
    msg.includes('econnreset') ||
    getErrorCode(err) === 'UnexpectedLambdaException';
  return isTransient;
}

async function signInWithPostAuthTimeoutRetries(
  email: string,
  password: string
): Promise<any> {
  const msBeforeAttempt = [0, 0, 5000, 8000] as const;

  for (let i = 0; i < msBeforeAttempt.length; i++) {
    const waitMs = msBeforeAttempt[i];
    if (waitMs > 0) {
      await sleep(waitMs);
    }
    try {
      return await Auth.signIn(email, password);
    } catch (err: unknown) {
      if (getErrorCode(err) === 'UserNotConfirmedException') {
        throw err;
      }
      if (!isPostAuthenticationLambdaSocketTimeoutError(err)) {
        throw err;
      }
      if (i === msBeforeAttempt.length - 1) {
        throw new Error(POST_AUTH_LAMBDA_SOCKET_TIMEOUT_FINAL_MESSAGE);
      }
    }
  }
  // Unreachable: the final iteration always returns or throws above.
  throw new Error(POST_AUTH_LAMBDA_SOCKET_TIMEOUT_FINAL_MESSAGE);
}

interface LoginFormProps {
  onSwitchToSignup: () => void;
  onSuccess: () => void;
  onVerificationNeeded: (email: string) => void;
}

export default function LoginForm({ onSwitchToSignup, onSuccess, onVerificationNeeded }: LoginFormProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [mfaUser, setMfaUser] = useState<any>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaSetupUser, setMfaSetupUser] = useState<any>(null);
  const [mfaSetupSecret, setMfaSetupSecret] = useState('');
  const [mfaSetupCode, setMfaSetupCode] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const user = await signInWithPostAuthTimeoutRetries(email, password);
      switch (user.challengeName) {
        case undefined:
          onSuccess();
          break;
        case 'SOFTWARE_TOKEN_MFA':
          setMfaUser(user);
          break;
        case 'MFA_SETUP': {
          const secret = await Auth.setupTOTP(user);
          setMfaSetupSecret(secret);
          setMfaSetupUser(user);
          break;
        }
        default:
          setError('Sign in requires an additional step that is not supported yet. Please contact support.');
      }
    } catch (err: any) {
      // Check if the error is due to unconfirmed user
      if (getErrorCode(err) === 'UserNotConfirmedException') {
        onVerificationNeeded(email);
        return;
      }
      setError(getErrorMessage(err) || 'An error occurred during sign in');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await Auth.confirmSignIn(mfaUser, mfaCode, 'SOFTWARE_TOKEN_MFA');
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Invalid verification code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaCancel = () => {
    setMfaUser(null);
    setMfaCode('');
    setError('');
  };

  const handleMfaSetupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await Auth.verifyTotpToken(mfaSetupUser, mfaSetupCode);
      await Auth.setPreferredMFA(mfaSetupUser, 'TOTP');
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Invalid verification code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaSetupCancel = () => {
    setMfaSetupUser(null);
    setMfaSetupSecret('');
    setMfaSetupCode('');
    setError('');
  };

  const mfaSetupUri = mfaSetupUser && mfaSetupSecret
    ? `otpauth://totp/PathboxAI:${encodeURIComponent(email)}?secret=${mfaSetupSecret}&issuer=PathboxAI`
    : '';

  if (mfaSetupUser) {
    return (
      <div className="w-full max-w-md mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-base-content mb-2">
            Set Up Two-Factor Authentication
          </h2>
          <p className="text-base-content/70">
            Your account requires an authenticator app. Scan the code below, then enter the
            6-digit code it generates.
          </p>
        </div>

        <form onSubmit={handleMfaSetupSubmit} className="space-y-6">
          {error && (
            <div className="alert alert-error">
              <span>{error}</span>
            </div>
          )}

          {mfaSetupUri && (
            <div className="flex justify-center">
              <QrCodeImage value={mfaSetupUri} />
            </div>
          )}

          <div className="form-control">
            <label className="label">
              <span className="label-text-alt">Manual entry key</span>
            </label>
            <code className="p-2 bg-base-200 rounded text-sm break-all">{mfaSetupSecret}</code>
          </div>

          <div className="form-control">
            <label className="label">
              <span className="label-text font-medium">Authentication Code</span>
            </label>
            <input
              type="text"
              value={mfaSetupCode}
              onChange={(e) => setMfaSetupCode(e.target.value)}
              className="input input-bordered w-full text-center text-lg tracking-widest"
              placeholder="Enter 6-digit code"
              maxLength={6}
              required
              autoFocus
              disabled={isLoading}
            />
          </div>

          <div className="form-control">
            <button
              type="submit"
              className="btn btn-primary w-full"
              disabled={isLoading || mfaSetupCode.length !== 6}
            >
              {isLoading ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  Verifying...
                </>
              ) : (
                'Verify & Sign In'
              )}
            </button>
          </div>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={handleMfaSetupCancel}
            className="link link-primary text-sm"
            disabled={isLoading}
          >
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  if (mfaUser) {
    return (
      <div className="w-full max-w-md mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-base-content mb-2">
            Two-Factor Authentication
          </h2>
          <p className="text-base-content/70">
            Enter the 6-digit code from your authenticator app
          </p>
        </div>

        <form onSubmit={handleMfaSubmit} className="space-y-6">
          {error && (
            <div className="alert alert-error">
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>{error}</span>
            </div>
          )}

          <div className="form-control">
            <label className="label">
              <span className="label-text font-medium">Authentication Code</span>
            </label>
            <input
              type="text"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value)}
              className="input input-bordered w-full text-center text-lg tracking-widest"
              placeholder="Enter 6-digit code"
              maxLength={6}
              required
              autoFocus
              disabled={isLoading}
            />
          </div>

          <div className="form-control">
            <button
              type="submit"
              className="btn btn-primary w-full"
              disabled={isLoading || mfaCode.length !== 6}
            >
              {isLoading ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  Verifying...
                </>
              ) : (
                'Verify'
              )}
            </button>
          </div>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={handleMfaCancel}
            className="link link-primary text-sm"
            disabled={isLoading}
          >
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-base-content mb-2">
          Sign In
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="alert alert-error">
            <svg 
              className="w-6 h-6" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
              />
            </svg>
            <span>{error}</span>
          </div>
        )}

        <div className="form-control">
          <label className="label">
            <span className="label-text font-medium">Email</span>
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input input-bordered w-full"
            placeholder="Enter your email"
            required
            disabled={isLoading}
          />
        </div>

        <div className="form-control">
          <label className="label">
            <span className="label-text font-medium">Password</span>
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input input-bordered w-full"
            placeholder="Enter your password"
            required
            disabled={isLoading}
          />
        </div>

        <div className="form-control">
          <button
            type="submit"
            className="btn btn-primary w-full"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="loading loading-spinner loading-sm"></span>
                Signing In...
              </>
            ) : (
              'Sign In'
            )}
          </button>
        </div>
      </form>

      <div className="mt-8 text-center">
        <p className="text-base-content/70">
          Don't have an account?{' '}
          <button
            onClick={onSwitchToSignup}
            className="link link-primary font-medium dark:text-primary-content dark:hover:text-primary-content/80"
            disabled={isLoading}
          >
            Create account
          </button>
        </p>
      </div>
    </div>
  );
}
